import { toast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";

// Nudges the user to reload when a NEWER app build has been deployed, so nobody sits
// for hours on stale app code after a release (which showed up as "404 / did you forget
// to add the page to the router?" when a new route existed on the server but not in the
// old bundle still running in the tab).
//
// How it works: it compares the hashed JS bundle referenced by a freshly-fetched
// index.html against the bundle THIS browser session booted with. index.html is served
// no-cache and the service worker has no fetch handler, so the fetch always reflects the
// live deployment. It is deliberately SW-independent.
//
// It NEVER force-reloads — that could discard unsaved form work — it shows a dismissible
// "Reload" toast so the user updates when they're ready.

const POLL_MS = 3 * 60 * 1000; // check every 3 minutes while the tab is visible
const BUNDLE_RE = /\/assets\/index-[A-Za-z0-9_-]+\.js/;

let bootBundle: string | null = null;
let nudged = false;

// The hashed entry bundle the live deployment currently references (null on any failure).
async function liveBundle(): Promise<string | null> {
  try {
    const res = await fetch("/", { cache: "no-store", headers: { Accept: "text/html" } });
    if (!res.ok) return null;
    const html = await res.text();
    const m = html.match(BUNDLE_RE);
    return m ? m[0] : null;
  } catch {
    return null;
  }
}

async function check() {
  if (nudged || document.visibilityState !== "visible") return;
  const live = await liveBundle();
  // Only act when we have a confident before/after and they genuinely differ.
  if (!bootBundle || !live || live === bootBundle) return;
  nudged = true;
  toast({
    title: "New version available",
    description: "Reload to get the latest update.",
    duration: 1000 * 60 * 60, // keep it up (~1h) until the user acts
    action: (
      <ToastAction altText="Reload the page to update" onClick={() => window.location.reload()}>
        Reload
      </ToastAction>
    ),
  });
}

export function startVersionWatch() {
  if (typeof window === "undefined") return;
  // Record the bundle this session is running, then watch for a newer deploy.
  liveBundle().then((b) => { bootBundle = b; });
  window.setInterval(check, POLL_MS);
  document.addEventListener("visibilitychange", check); // re-check the moment the tab is refocused
}
